
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Alert, Stack, Typography } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { useMoneyFormatter } from '@utils/numberUtils';
import { useIntl } from 'react-intl';
import { useAtom } from 'jotai';
import { useGetProjectBudgetQuery } from '../../../gql';
import { useCurrentProject } from '../../projects/utils/useCurrentProject';
import { budgetChartState } from '../atoms';

export const BudgetChart = () => {
  const { projectId, canViewBudgetAndExpenses } = useCurrentProject();
  const { data: budgetQuery } = useGetProjectBudgetQuery({ id: projectId });
  const budget = budgetQuery?.projectBudget;
  const [isExpanded, setIsExpanded] = useAtom(budgetChartState);

  const budgetDetails = {
    initialBudget: budget?.totalBudgetedAmount,
    budgetIncludingOverrun: budget?.totalBudgetedAmount + budget?.budgetOverrun,
    budgetedExpenses: budget?.totalEffectiveAmount - budget?.budgetOverrun,
    remainingAmount: budget?.totalBudgetedAmount + budget?.budgetOverrun - budget?.totalEffectiveAmount,
    budgetOverrun: budget?.budgetOverrun
  };

  const { formatMessage } = useIntl();

  const formatMoney = useMoneyFormatter();

  return (
    <Stack gap={2}>
      {!canViewBudgetAndExpenses && <Alert severity='warning'>{formatMessage({ id: 'You have not been granted access to view expenses. You may contact the project administrator to request access.' })}</Alert>}

      <Accordion expanded={isExpanded} onChange={() => setIsExpanded((prev) => !prev)}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="budget-content"
          id="budget-header"
        >
          <Stack gap={2}>
            {(isExpanded || budgetDetails.budgetIncludingOverrun <= budgetDetails.initialBudget) &&
            <span>
              <Typography variant='h4' display={'inline'}>{formatMessage({ id: 'Initial budget: ' })}</Typography>
              <Typography variant='h4' color={'primary'} display={'inline'}>{formatMoney(budgetDetails.initialBudget, false)}</Typography>
            </span>
            }
            {budgetDetails.budgetIncludingOverrun > budgetDetails.initialBudget && 
            <span>
              <Typography variant='h5' display={'inline'}>{formatMessage({ id: 'Budget (including overrun): ' })}</Typography>
              <Typography variant='h5' color={'warning.main'} display={'inline'}>{formatMoney(budgetDetails.budgetIncludingOverrun, false)}</Typography>
            </span>}
          </Stack>
        </AccordionSummary>
        <AccordionDetails>

          <BarChart
            sx={{ padding: 1 }}
            axisHighlight={{ y: 'line' }}
            slotProps={{

              bar: {
                style: { width: 80 },
              },

              legend: {
                direction: 'vertical'
              },

            }}
            xAxis={[{
              label: formatMessage({ id: 'Budget' }),
              scaleType: 'band' as const,
              data: [''],
              tickSize: 2,
              position: 'bottom'
            }]}

            series={[
              { data: [budgetDetails.budgetedExpenses ?? 0], color: '#3498db', valueFormatter: (p) => p ? formatMoney(p, false) : '', stack: 'A', label: formatMessage({ id: 'Budgeted expenses' }) },
              { data: [budgetDetails.remainingAmount ?? 0], color: '#2ecc71', valueFormatter: (p) => p ? formatMoney(p, false) : '', stack: 'A', label: formatMessage({ id: 'Remaining budget' }) },
              { data: [budgetDetails.budgetOverrun ?? 0], color: '#e74c3c', valueFormatter: (p) => p ? formatMoney(p, false) : '', stack: 'A', label: formatMessage({ id: 'Budget overrun' }) },

            ]}

            width={450}
            height={250}
          />
        </AccordionDetails>
      </Accordion>
    </Stack>);
};